import { json } from '@aurelia/fetch-client';
import { IConfigService } from './config-service';
import { ErrorMessages } from '../resources/constants';
import { IHttpConfig } from '../resources/http-config';
import { ResponseResults } from '../resources/models/response-results';
import { DI, IHttpClient } from 'aurelia';

/* eslint-disable prefer-const */

const ABN_LOOKUP_ENDPOINT = '/abnlookup';
const AUTORATE_ENDPOINT = '/autorate/scheme';
const CAPTCHA_TOKEN = '/recaptcha';
const DOCPACK_ENDPOINT = '/docpack/generation/scheme';
const BPOINT_ENDPOINT = '/bpoint/authkey';
const FORM_TYPE = 'application/x-www-form-urlencoded';
const REQUEST_TYPE_POST = 'POST';
const REQUEST_TYPE_PUT = 'PUT';

export const IApiService = DI.createInterface<IApiService>('IApiService', (x) =>
    x.singleton(Api)
);
export interface IApiService extends Api {}

export class Api {
    cachedSessionForms = [];
    protected errorMessages = ErrorMessages;
    private clientApiHost;
    private appClient;

    constructor(
        @IConfigService readonly config: IConfigService,
        @IHttpClient protected http: IHttpClient,
        @IHttpConfig readonly httpConfig: IHttpConfig
    ) {
        this.http = httpConfig.configure(http);
        this.config = config;
        this.errorMessages = ErrorMessages;

        this.clientApiHost = this.config.get('clientApiHost');
        this.appClient = this.config.get('appClient');
    }

    verifyCaptchaToken(token) {
        return this.http
            .fetch(`${this.clientApiHost}${CAPTCHA_TOKEN}`, {
                method: REQUEST_TYPE_POST,
                headers: {
                    'Content-Type': FORM_TYPE,
                },
                body: $.param({ 'g-recaptcha-response': token }),
            })
            .then((response) => response.json());
    }

    createSession(schemeCode) {
        const url = `${this.clientApiHost}/schemes/${schemeCode}/sessions`;
        return this.http
            .fetch(url, { method: REQUEST_TYPE_POST })
            .then((response) => response.json())
            .catch((e) => {
                throw new Error(
                    this.errorMessages.CreateSessionError.ErrorMessage
                );
            });
    }

    getSession(schemeCode, schemeSessionId) {
        const url = `${this.clientApiHost}/schemes/${schemeCode}/sessions/${schemeSessionId}`;
        return this.http
            .fetch(url)
            .then((response) => response.json())
            .catch((e) => {
                throw new Error(this.errorMessages.SessionError.ErrorCode);
            });
    }

    getSessionForms(schemeCode, schemeSessionId, schemeSessionFormId = null) {
        let url = `${
            this.clientApiHost
        }/schemes/${schemeCode}/sessions/${schemeSessionId}/forms?${
            Math.random() * 10000000000000000
        }`;

        if (schemeSessionFormId !== null) {
            url += `/${schemeSessionFormId}`;
        }

        return this.http
            .fetch(url)
            .then((response) => response.json())
            .then((response) => (this.cachedSessionForms = response))
            .catch((e) => {
                throw new Error(this.errorMessages.SessionFormsError.ErrorCode);
            });
    }

    updateSession(schemeCode) {
        const url = `${this.clientApiHost}/schemes/${schemeCode}/sessions`;
        return this.http
            .fetch(url, {
                method: REQUEST_TYPE_PUT,
            })
            .then((response) => response.json());
    }

    postForm(schemeCode, schemeSessionId, body) {
        let url = `${this.clientApiHost}/schemes/${schemeCode}/sessions/${schemeSessionId}/forms`;

        return this.http
            .fetch(url, {
                method: REQUEST_TYPE_POST,
                headers: {
                    Accept: 'application/json, application/xml, text/plain, text/html, *.*',
                    'Content-Type': FORM_TYPE,
                },
                //https://stackoverflow.com/questions/36067757/post-x-www-form-urlencoded-content-with-aurelia-fetch-client
                body: $(body).serialize(),
            })
            .then((response) => response.json())
            .catch((e) => {
                throw new Error(
                    this.errorMessages.SaveSessionError.ErrorMessage
                );
            });
    }

    putForm(schemeCode, schemeSessionId, formGuid, body) {
        let url = `${this.clientApiHost}/schemes/${schemeCode}/sessions/${schemeSessionId}/forms/${formGuid}`;

        return this.http
            .fetch(url, {
                method: REQUEST_TYPE_PUT,
                headers: {
                    Accept: 'application/json, application/xml, text/plain, text/html, *.*',
                    'content-type': FORM_TYPE,
                },
                //https://stackoverflow.com/questions/36067757/post-x-www-form-urlencoded-content-with-aurelia-fetch-client
                body: $(body).serialize(),
            })
            .then((response) => response.json())
            .catch((e) => {
                throw new Error(
                    this.errorMessages.SaveSessionError.ErrorMessage
                );
            });
    }

    getAbn(val) {
        return this.http
            .fetch(`${this.clientApiHost}${ABN_LOOKUP_ENDPOINT}/${val}`)
            .then((response) => response.json())
            .catch((e) => {
                throw new Error(this.errorMessages.ABNSearchTimeOut);
            });
    }

    async postAutoRateRequest(data) {
        const responseModel = new ResponseResults();

        try {
            const url = `${this.clientApiHost}/schemes/${data.SchemeGuid}/sessions/${data.SchemeSessionId}${AUTORATE_ENDPOINT}`;
            const autoRateResult = await this.http.fetch(url, {
                method: REQUEST_TYPE_POST,
                body: json(data),
            });

            responseModel.setStatus(autoRateResult.status);
            responseModel.setResults(await autoRateResult.json());

            if (responseModel.getStatus() !== 201) {
                throw new Error(responseModel.getResults());
            }
        } catch (error) {
            throw new Error(
                `Rating failed. Server Error: ${JSON.stringify(
                    responseModel.getResults()
                )}`
            );
        }

        return responseModel;
    }

    getSchemeData(schemeGuid) {
        const schemeUrl = `${this.clientApiHost}/schemes/${schemeGuid}`;
        return this.http
            .fetch(`${schemeUrl}`)
            .then((res) => res.json())
            .catch((e) => {
                throw new Error(this.errorMessages.SchemeError.ErrorCode);
            });
    }

    postCreatePolicy(schemeGuid, sessionId) {
        const schemeUrl = `${this.clientApiHost}/schemes/${schemeGuid}/sessions/${sessionId}/policy`;
        return this.http
            .fetch(schemeUrl, {
                method: REQUEST_TYPE_POST,
            })
            .then((response) => response.json());
    }

    getQuestionsForScheme(schemeGuid) {
        const schemeUrl = `${this.clientApiHost}/schemes/${schemeGuid}/questions`;
        return this.http
            .fetch(`${schemeUrl}`)
            .then((res) => res.json())
            .catch((e) => {
                throw new Error(this.errorMessages.QuestionError.ErrorCode);
            });
    }

    getSessionQuestions(schemeGuid, schemeSessionId) {
        let url = `${this.clientApiHost}/schemes/${schemeGuid}/sessions/${schemeSessionId}/questions`;

        return this.http
            .fetch(url)
            .then((response) => response.json())
            .catch((e) => {
                throw new Error(
                    this.errorMessages.SessionQuestionsError.ErrorCode
                );
            });
    }

    postDocPackGeneration(data) {
        return this.http.fetch(`${this.clientApiHost}${DOCPACK_ENDPOINT}`, {
            method: REQUEST_TYPE_POST,
            body: json(data),
        });
        //.then(response => response.json());
    }

    async postBpointVerifyAuthKey() {
        const postAuth = await this.http.fetch(
            `${this.clientApiHost}/bpoint/processtxn/authkey`,
            {
                method: 'POST',
            }
        );

        const authResult = await postAuth.json();

        return authResult;
    }

    async postBpointVerifyTransaction(verifyAuthKey, data) {
        const result = await fetch(
            `https://www.bpoint.com.au/webapi/v3/txns/withauthkey/${verifyAuthKey}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            }
        );
        const authResult = await result.json();
        return authResult;
    }

    async postBpointVerifyTransactionResult(resultKey) {
        const postVerify = await this.http.fetch(
            `${this.clientApiHost}/bpoint/processtxn/result/verify/${resultKey}`,
            {
                method: 'POST',
            }
        );

        const verifyResult = await postVerify.json();
        return verifyResult;
    }

    async postBpointPaymentAuthKey(invoiceId, cardType) {
        const postAuth = await this.http.fetch(
            `${this.clientApiHost}/bpoint/processtxn/authkey/${invoiceId}?cardType=${cardType}`,
            {
                method: 'POST',
            }
        );

        const authResult = await postAuth.json();

        if (postAuth.status === 400) {
            // invalid invoiceId
            if (
                authResult.hasOwnProperty('invalidInvoice') ||
                authResult.hasOwnProperty('invoiceId')
            ) {
                return -1;
            }

            // invalid invoiceId for client
            if (authResult.hasOwnProperty('clientNotValid')) {
                return -2;
            }

            // invoice already paid
            if (authResult.hasOwnProperty('invoicePaid')) {
                return -3;
            }
        }

        return authResult;
    }

    async postBpointPaymentTransactionResult(resultKey) {
        const postVerify = await this.http.fetch(
            `${this.clientApiHost}/bpoint/processtxn/result/${resultKey}`,
            {
                method: 'POST',
            }
        );
        const verifyResult = await postVerify.json();
        return verifyResult;
    }

    async getPremiumFunderModel(policyVersionId) {
        const funderModel = await this.http.fetch(
            `${this.clientApiHost}/premiumfunder/${policyVersionId}`
        );
        const funderModelResult = await funderModel.json();
        return funderModelResult;
    }

    async postPremiumFunder(policyVersionId, data) {
        const url = `${this.clientApiHost}/premiumfunder/${policyVersionId}`;
        const result = await this.http.fetch(url, {
            method: REQUEST_TYPE_POST,
            headers: {
                'Content-Type': 'application/json',
            },
            body: json(data),
        });

        return await result.json();
    }
}
